import { useRef, useEffect } from 'react';

const STEPS = [
  {
    number: '01',
    icon: 'ri-chat-smile-2-line',
    title: 'Contato Inicial',
    desc: 'Você entra em contato pelo WhatsApp ou formulário. Resposta ágil e atenciosa para entender sua demanda de forma preliminar.',
  },
  {
    number: '02',
    icon: 'ri-search-eye-line',
    title: 'Entendimento da Demanda',
    desc: 'Conversa detalhada para compreender sua situação, contexto e objetivos. Escuta ativa e análise preliminar da questão jurídica.',
  },
  {
    number: '03',
    icon: 'ri-file-search-line',
    title: 'Análise Detalhada',
    desc: 'Estudo aprofundado do caso com pesquisa técnica, análise da legislação aplicável e identificação dos riscos e possibilidades.',
  },
  {
    number: '04',
    icon: 'ri-lightbulb-flash-line',
    title: 'Orientação e Soluções',
    desc: 'Apresentação clara das orientações jurídicas, estratégias recomendadas e encaminhamentos precisos para a resolução da sua questão.',
  },
];

export default function HowItWorksSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.1 }
    );
    sectionRef.current?.querySelectorAll('.anim').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="como-funciona" className="py-32 bg-white" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-20 anim opacity-0 translate-y-8 transition-all duration-700">
          <p className="text-[#C6935A] text-xs font-600 tracking-[3px] uppercase mb-4">
            Processo
          </p>
          <h2 className="text-4xl lg:text-5xl font-700 text-[#1F4B63] leading-tight mb-4">
            Como Funciona
          </h2>
          <div className="w-16 h-0.5 bg-[#C6935A] mx-auto mt-4 mb-6" />
          <p className="text-[#4A5568] text-lg max-w-xl mx-auto">
            Um processo simples, transparente e eficiente para garantir a melhor orientação jurídica.
          </p>
        </div>

        {/* Desktop Timeline */}
        <div className="hidden lg:grid lg:grid-cols-4 gap-0 relative">
          {/* Horizontal connector line */}
          <div className="absolute top-10 left-[12.5%] right-[12.5%] h-px bg-[#C6935A]/30 z-0" />

          {STEPS.map((step, i) => (
            <div
              key={step.number}
              className="anim opacity-0 translate-y-8 transition-all duration-700 flex flex-col items-center text-center px-6"
              style={{ transitionDelay: `${i * 150}ms` }}
            >
              {/* Circle with Number */}
              <div className={`relative z-10 w-20 h-20 flex items-center justify-center rounded-full border-2 mb-8 flex-shrink-0 ${
                i === STEPS.length - 1
                  ? 'bg-[#C6935A] border-[#C6935A]'
                  : 'bg-white border-[#C6935A]'
              }`}>
                <span className={`text-2xl font-700 ${i === STEPS.length - 1 ? 'text-white' : 'text-[#C6935A]'}`}>{step.number}</span>
              </div>

              <h3 className="text-[#1F4B63] font-700 text-lg mb-3">{step.title}</h3>
              <p className="text-[#4A5568] text-sm leading-relaxed">{step.desc}</p>
            </div>
          ))}
        </div>

        {/* Mobile Timeline */}
        <div className="lg:hidden relative">
          <div className="absolute left-9 top-0 bottom-0 w-px bg-[#C6935A]/30" />

          {STEPS.map((step, i) => (
            <div
              key={step.number}
              className="anim opacity-0 translate-y-8 transition-all duration-700 flex gap-8 mb-12"
              style={{ transitionDelay: `${i * 150}ms` }}
            >
              <div className={`relative z-10 flex-shrink-0 w-16 h-16 flex items-center justify-center rounded-full border-2 mt-1 ${
                i === STEPS.length - 1
                  ? 'bg-[#C6935A] border-[#C6935A]'
                  : 'bg-white border-[#C6935A]'
              }`}>
                <span className={`text-xl font-700 ${i === STEPS.length - 1 ? 'text-white' : 'text-[#C6935A]'}`}>{step.number}</span>
              </div>
              <div className="pt-1">
                <h3 className="text-[#1F4B63] font-700 text-lg mb-2">{step.title}</h3>
                <p className="text-[#4A5568] text-sm leading-relaxed">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Block */}
        <div className="mt-20 anim opacity-0 translate-y-8 transition-all duration-700 delay-700">
          <div className="bg-gradient-to-r from-[#1F4B63] to-[#173A4D] rounded-2xl p-12 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-5">
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#C6935A] rounded-full translate-x-1/2 -translate-y-1/2" />
            </div>
            <div className="relative">
              <p className="text-[#D4A873] text-xs font-600 tracking-[3px] uppercase mb-4">Pronto para começar?</p>
              <h3 className="text-white text-3xl lg:text-4xl font-700 mb-4 leading-tight">
                Fale diretamente com o advogado e receba<br className="hidden lg:block" /> orientação jurídica segura.
              </h3>
              <p className="text-white/70 text-base mb-10 max-w-xl mx-auto">
                Atendimento personalizado, análise técnica e segurança jurídica em cada orientação.
              </p>
              <div className="flex justify-center">
                <a
                  href="https://wa.me/5579999778899?text=Olá,%20Dr.%20Diogo,%20gostaria%20de%20solicitar%20uma%20consulta%20jurídica."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-3 bg-[#C6935A] hover:bg-[#A8763F] text-white font-600 px-10 py-4 rounded-full transition-all duration-300 hover:-translate-y-0.5 whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-xl" />
                  Iniciar pelo WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
